<template>
  <div class="lead_capture_form">
    <UIAlert v-if="sugarDown">
      {{ sugarDownMessage }}
    </UIAlert>

    <FormWizard
      v-else
      recaptcha
      :error="!!error"
      :loading="loading"
      @submit="onSubmit"
    >
      <AgilityField prefix="lead-cap" />

      <HiddenField name="form-type" :modelValue="formType" />
      <HiddenField name="csrfmiddlewaretoken" :modelValue="csrf" />
      <HiddenField :name="`lead-cap-rdr0`" :modelValue="rdr0" />
      <HiddenField :name="`lead-cap-rdr1`" :modelValue="rdr1" />
      <HiddenField :name="`lead-cap-rdr2`" :modelValue="rdr2" />
      <HiddenField :name="`lead-cap-prod`" :modelValue="productId" />
      <HiddenField :name="`lead-cap-form_source`" :modelValue="formSource" />
      <HiddenField :name="`lead-cap-campaign_id`" :modelValue="campaignId" />
      <HiddenField :name="`lead-cap-lead_type`" :modelValue="leadType" />
      <HiddenField name="role-init" :modelValue="initialRole" />
      <HiddenField name="exclude-roles" :modelValue="excludeRoles" />
      <HiddenField name="query-school-size" :modelValue="schoolSize" />

      <h2 v-if="title">{{ title }}</h2>

      <!-- Fields slot -->

      <FormStep terms-field>
        <slot name="fields"></slot>
      </FormStep>

      <template #submit="submitProps">
        <SubmitButton v-bind="submitProps">
          {{ submitLabel }}
        </SubmitButton>
      </template>
    </FormWizard>
  </div>
</template>

<script lang="ts">
type LeadCaptureFormProps = {
  formType: string;
  title?: string;
  rdr0?: string;
  rdr1?: string;
  rdr2?: string;
  productId?: string;
  formSource?: string;
  campaignId?: string;
  leadType?: string;
  excludeRoles?: string;
  initialRole?: string;
  schoolSize?: string;
  sugarDown?: boolean;
  sugarDownMessage?: string;
  submitLabel: string;
  csrf: string;
};
</script>

<script lang="ts" setup>
import {
  getConversionGoogleEvent,
  sendGoogleEvents,
} from "@/common/google-events";
import HiddenField from "@/components/fields/HiddenField.vue";
import { sendFormData } from "@/lib/submitForm";
import { useCookies } from "@vueuse/integrations/useCookies";
import { GenericObject } from "vee-validate";
import { computed, ref } from "vue";
import UIAlert from "../UI/UIAlert.vue";
import FormStep from "./FormStep.vue";
import FormWizard from "./FormWizard.vue";
import SubmitButton from "./SubmitButton.vue";
import { RoleType, useContactCaptureProvider } from "./useContactCapture";

const props = defineProps<LeadCaptureFormProps>();
const loading = ref(false);
const error = ref(false);

useContactCaptureProvider("lead-cap-", {
  role: <RoleType>props.initialRole,
  excludeRoles: props.excludeRoles?.split(",") as RoleType[],
});

const cookies = useCookies(["campaign_id"]);
const campaignIdCookie = cookies.get("campaign_id");

const campaignId = computed<string | null>(
  () => props.campaignId ?? campaignIdCookie
);

const onSubmit = async (values: GenericObject) => {
  const data: Record<string, unknown> = {
    ...values,
  };

  loading.value = true;

  try {
    await sendFormData("/forms/", data);
  } catch (e) {
    loading.value = false;
    error.value = true;
    console.error("Error while submitting the form.", e);
    return;
  }

  let redirect =
    data["role"] === "School" && props.rdr2 ? props.rdr2 : props.rdr1;

  if (!redirect) {
    loading.value = false;
    error.value = true;
    throw "Missing redirect URL.";
  }

  await sendGoogleEvents(getConversionGoogleEvent("lead"));

  document.location.href = redirect;
};
</script>
